import axios from 'axios';
import { api } from './api';

export interface LegalItem {
  id: number;
  name: string;
  legal_text: string;
  color: string;
  is_active: boolean;
  country_code: string;
  country_name: string;
  created_at: string;
  modified_at: string;
}

export interface LegalTableItem {
  id: number;
  checked: boolean;
  disabled: boolean;
  data: {
    name: string;
    legal_text: string;
    color: string;
    is_active: boolean;
  };
}

export type KeyNameFields = 'id' | 'name' | 'legal_text' | 'color' | 'is_active' | 'actions';

export interface LegalListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: LegalItem[];
}

export interface LegalListParams {
  page?: number;
  page_size?: number;
  search?: string;
  is_active?: boolean;
  ordering?: string;
}

export interface CreateLegalData {
  name: string;
  legal_text: string;
  color: string;
}

export const legalsService = {
  getLegalsList: async (params?: LegalListParams): Promise<LegalListResponse> => {
    try {
      const response = await api.get('/legals/list', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener lista de legales:', error);

      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Error al comunicarse con el servidor');
      }

      throw new Error('Error inesperado al obtener la lista de legales');
    }
  },

  createLegal: async (data: CreateLegalData): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.post('/legal', data);
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Error al crear legal:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: (typeof error.response?.data?.error === 'string'
            ? error.response?.data?.error
            : Object.values(error.response?.data?.error || {})[0] as string) || 'Error al comunicarse con el servidor',
        };
      }

      return {
        success: false,
        message: 'Error inesperado al crear el legal',
      };
    }
  },

  getLegal: async (legalId: string): Promise<{ success: boolean; message: string; data?: LegalItem }> => {
    try {
      const response = await api.get(`/legals/${legalId}`);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data ?? response.data,
      };
    } catch (error) {
      console.error('Error al obtener legal:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor',
        };
      }

      return {
        success: false,
        message: 'Error inesperado al obtener el legal',
      };
    }
  },

  updateLegal: async (legalId: string, data: Partial<CreateLegalData>): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.patch(`/legals/${legalId}`, data);
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Error al actualizar legal:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor',
        };
      }

      return {
        success: false,
        message: 'Error inesperado al actualizar el legal',
      };
    }
  },

  deleteLegal: async (legalId: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.delete(`/legals/${legalId}`);
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Error al eliminar legal:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor',
        };
      }

      return {
        success: false,
        message: 'Error inesperado al eliminar el legal',
      };
    }
  },
};
